import React, { forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState, } from 'react';
import clsx from 'clsx';
import { assignRef, safeFocus, useClickOutside, useComposedRefs, useControlledValue, useCurrent, useEscKeyHandler, useFloatingElement, useFocusScope, useFocusTrap, useAnimation, useResponsiveProp, DeferredPromise, getSide, callMultiple, } from '../../utils';
import { Portal } from '../Portal';
import { Box } from '../Box';
import { useModalContext } from '../Modal';
import './Popover.css';
export const Popover = forwardRef(function Popover(props, ref) {
    const { children, elementType = 'div', className, placement = 'bottom-start', animationAnchor: userAnimationAnchor, autoSize, trigger, open: userOpen, defaultOpen = false, onClose, onOpen, onChangeOpen, onAfterClose, onMouseDown, onClick, onBlur, tabIndex = -1, autoFocus = true, containFocus, anchorElement: userAnchorElement, anchorPosition, asyncApiRef, size = ({ elements }) => {
        const { width } = elements.anchor.getBoundingClientRect();
        return {
            width: autoSize ? `${width}px` : undefined,
        };
    }, scrollRef, ...rest } = props;
    const { initialized: modalInitialized } = useModalContext();
    const promiseRef = useRef();
    const onChange = useCallback((open) => {
        if (open) {
            onOpen === null || onOpen === void 0 ? void 0 : onOpen();
        }
        else {
            onClose === null || onClose === void 0 ? void 0 : onClose();
        }
        onChangeOpen === null || onChangeOpen === void 0 ? void 0 : onChangeOpen(open);
    }, [onClose, onOpen, onChangeOpen]);
    const [open, setOpen] = useControlledValue({
        value: userOpen,
        onChange,
        defaultValue: defaultOpen,
    });
    const [popoverElement, setPopoverElement] = useState(null);
    const [anchorElement, setAnchorElement] = useState(null);
    const id = useId();
    const triggerElement = typeof trigger === 'string' ? (React.createElement("span", null, trigger)) : trigger ? (React.Children.only(trigger)) : null;
    const responsivePlacement = useResponsiveProp(placement);
    const { refs, anchor, floating, floatingStyles, anchorPoint, isPositioned, } = useFloatingElement({
        placement: responsivePlacement,
        preferFlip: true,
        size,
        open,
        watchStyleChanges: anchorPosition != null,
    });
    const animationAnchor = userAnimationAnchor !== null && userAnimationAnchor !== void 0 ? userAnimationAnchor : getSide(anchorPoint !== null && anchorPoint !== void 0 ? anchorPoint : 'bottom');
    const preventBlur = useRef(false);
    const preventFocus = useRef(false);
    const activeElementRef = useRef(null);
    const animationRef = useRef(null);
    const focusScope = useFocusScope(refs.floating, !containFocus ? activeElementRef : undefined);
    const popoverRef = useComposedRefs(floating, setPopoverElement, ref);
    const wrapperRef = useComposedRefs(animationRef, scrollRef);
    const { canMount, animationClassNames } = useAnimation(animationRef, open, {
        canAnimate: isPositioned || open === false,
        onAfterAnimationEnded: () => {
            var _a;
            if (!open) {
                onAfterClose === null || onAfterClose === void 0 ? void 0 : onAfterClose();
            }
            (_a = promiseRef.current) === null || _a === void 0 ? void 0 : _a.resolve(open);
        },
    });
    useFocusTrap(focusScope);
    useClickOutside(popoverElement, () => {
        preventFocus.current = true;
        setOpen(false);
    }, open);
    useEscKeyHandler(() => {
        setOpen(false);
    }, (e) => {
        var _a;
        return Boolean((_a = refs.floating.current) === null || _a === void 0 ? void 0 : _a.contains(e.target));
    });
    const autoFocusRef = useCurrent(autoFocus);
    useEffect(() => {
        const timeout = window.setTimeout(() => {
            preventFocus.current = false;
            if (open) {
                activeElementRef.current = document.activeElement;
                if (autoFocusRef.current)
                    focusScope.focusFirstDescendant();
            }
            else {
                activeElementRef.current = null;
            }
        }, 0);
        return () => {
            clearTimeout(timeout);
            // return focus to the previous element, but only when autoFocus is set to true
            // eslint-disable-next-line react-hooks/exhaustive-deps
            if (autoFocusRef.current && !preventFocus.current) {
                safeFocus(activeElementRef.current);
            }
        };
    }, [autoFocusRef, focusScope, open]);
    useEffect(() => {
        if (userAnchorElement) {
            assignRef(anchor, userAnchorElement);
            return () => {
                assignRef(anchor, null);
            };
        }
        return;
    }, [anchor, userAnchorElement]);
    const asyncApi = useMemo(() => ({
        toggleOpen: () => {
            promiseRef.current = new DeferredPromise();
            setOpen((open) => !open);
            return promiseRef.current.promise;
        },
        open: () => {
            promiseRef.current = new DeferredPromise();
            setOpen(true);
            return promiseRef.current.promise;
        },
        close: () => {
            promiseRef.current = new DeferredPromise();
            setOpen(false);
            return promiseRef.current.promise;
        },
    }), [setOpen]);
    const floatingZIndex = useMemo(() => anchorElement
        ? getComputedStyle(anchorElement).getPropertyValue('--mollie-ui-floating-z-index')
        : undefined, [anchorElement]);
    if (asyncApiRef) {
        asyncApiRef.current = asyncApi;
    }
    return (React.createElement(React.Fragment, null,
        anchorPosition && (React.createElement(Box, { position: "fixed", insetBlockStart: anchorPosition[1], insetInlineStart: anchorPosition[0], ref: (node) => {
                assignRef(anchor, node);
                if (!triggerElement) {
                    setAnchorElement(node);
                }
            } })),
        triggerElement &&
            React.cloneElement(triggerElement, {
                'aria-expanded': open,
                'aria-controls': open && id,
                'aria-haspopup': true,
                ref: (node) => {
                    const { ref } = triggerElement;
                    if (!userAnchorElement && !anchorPosition) {
                        assignRef(anchor, node);
                    }
                    assignRef(ref, node);
                    setAnchorElement(node);
                },
                onMouseDown: () => {
                    preventBlur.current = true;
                },
                onClick: () => {
                    setOpen((open) => !open);
                },
                onBlur: (e) => {
                    var _a;
                    if (preventBlur.current) {
                        preventBlur.current = false;
                        return;
                    }
                    if (!((_a = refs.floating.current) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget)) &&
                        e.relatedTarget !== refs.anchor.current) {
                        preventFocus.current = true;
                        setOpen(false);
                    }
                },
                ...triggerElement.props,
            }),
        canMount && (React.createElement(Portal, null,
            React.createElement(Box.Restricted, { ...rest, elementType: elementType, role: "dialog", "aria-modal": false, className: clsx('mollie-ui-popover', animationClassNames, className), id: id, style: {
                    ...floatingStyles,
                    '--mollie-ui-floating-z-index': modalInitialized
                        ? 600
                        : floatingZIndex,
                }, tabIndex: tabIndex, onBlur: callMultiple((e) => {
                    var _a;
                    if (preventBlur.current) {
                        preventBlur.current = false;
                        return;
                    }
                    if (!((_a = refs.floating.current) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget))) {
                        preventFocus.current = true;
                        setOpen(false);
                    }
                }, onBlur), onMouseDown: callMultiple(() => {
                    preventBlur.current = true;
                }, onMouseDown), onClick: callMultiple((e) => {
                    e.stopPropagation();
                }, onClick), ref: popoverRef },
                React.createElement("div", { className: clsx('mollie-ui-popover-wrapper', {
                        [`mollie-ui-animation-anchor-${animationAnchor}`]: animationAnchor,
                    }, animationClassNames), ref: wrapperRef }, children))))));
});
Popover.displayName = 'Popover';
//# sourceMappingURL=Popover.js.map